'use client';
import React, { useEffect, useRef, useState } from 'react';

const aiConversations = [
  {
    question: 'Quanto vendi hoje?',
    answer: 'Hoje você faturou R$ 4.892,50 em 127 vendas. Isso representa um aumento de 18% em relação à sexta-feira passada. 🎉',
    chart: [60, 75, 45, 90, 55, 80, 70],
  },
  {
    question: 'Qual meu lucro este mês?',
    answer: 'Seu lucro líquido em julho é de R$ 18.640. Margem de 38,6%. Os maiores custos são insumos (42%) e pessoal (28%).',
    chart: [45, 55, 65, 50, 80, 70, 85],
  },
  {
    question: 'Quais produtos estão acabando?',
    answer: 'Atenção! 3 produtos com estoque crítico: Farinha de Trigo, Azeite Extra Virgem e Refrigerante 2L. Reposição urgente.',
    chart: [20, 15, 10, 8, 5, 3, 2],
  },
  {
    question: 'Como aumentar meu faturamento?',
    answer: 'Análise sugere: Combo X-Burguer + Fritas tem conversão de 67%. Ativar promoção às quartas pode gerar +R$ 2.400/mês.',
    chart: [50, 60, 75, 85, 90, 95, 100],
  },
];

export default function AISection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [typing, setTyping] = useState(false);
  const [displayedAnswer, setDisplayedAnswer] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    const full = aiConversations?.[activeIdx]?.answer;
    setDisplayedAnswer('');
    setTyping(true);
    let i = 0;
    const interval = setInterval(() => {
      if (i < full?.length) {
        setDisplayedAnswer(full?.slice(0, i + 1));
        i++;
      } else {
        setTyping(false);
        clearInterval(interval);
      }
    }, 18);
    return () => clearInterval(interval);
  }, [activeIdx]);

  const conv = aiConversations?.[activeIdx];
  const maxBar = Math.max(...conv?.chart);

  return (
    <section className="py-24 px-4 sm:px-6 relative overflow-hidden">
      <div className="absolute top-1/2 right-0 w-96 h-96 blob-secondary opacity-30 rounded-full -translate-y-1/2" />
      <div ref={ref} className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-secondary/30 bg-secondary/10 text-xs font-bold uppercase tracking-widest text-secondary mb-6">
              <span className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
              Inteligência Artificial
            </span>
            <h2 className="text-section-title font-black text-foreground mb-6">
              Seu assistente inteligente{' '}
              <span className="gradient-text">de gestão.</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Faça perguntas em linguagem natural e receba respostas instantâneas com dados reais do seu negócio, gráficos e recomendações acionáveis.
            </p>

            {/* Question buttons */}
            <div className="flex flex-wrap gap-3">
              {aiConversations?.map((conv, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIdx(i)}
                  className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    activeIdx === i
                      ? 'bg-secondary text-white shadow-lg'
                      : 'bg-muted/60 text-muted-foreground hover:text-foreground hover:bg-muted border border-border'
                  }`}
                >
                  {conv?.question}
                </button>
              ))}
            </div>
          </div>

          {/* Right: AI chat interface */}
          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="glass-card rounded-2xl overflow-hidden shadow-2xl">
              {/* Chat header */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-border bg-muted/30">
                <div className="w-8 h-8 rounded-full bg-secondary/20 border border-secondary/30 flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-secondary">
                    <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">IA NegócioFácil</p>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                    <span className="text-xs text-primary">Online</span>
                  </div>
                </div>
              </div>

              {/* Chat body */}
              <div className="p-5 space-y-4 min-h-[320px]">
                {/* User question */}
                <div className="flex justify-end">
                  <div className="bg-secondary/20 border border-secondary/30 rounded-2xl rounded-tr-sm px-4 py-3 max-w-xs">
                    <p className="text-sm text-foreground font-medium">{conv?.question}</p>
                  </div>
                </div>

                {/* AI answer */}
                <div className="flex justify-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs">🤖</span>
                  </div>
                  <div className="bg-muted/50 border border-border rounded-2xl rounded-tl-sm px-4 py-3 max-w-xs">
                    <p className="text-sm text-foreground leading-relaxed">
                      {displayedAnswer}
                      {typing && <span className="cursor-blink inline-block w-0.5 h-4 bg-primary ml-0.5 align-middle" />}
                    </p>
                  </div>
                </div>

                {/* Mini chart */}
                {!typing && (
                  <div className="ml-10 bg-muted/30 rounded-xl p-3 border border-border">
                    <div className="flex items-end gap-1 h-16">
                      {conv?.chart?.map((h, i) => (
                        <div
                          key={i}
                          className="flex-1 rounded-sm transition-all duration-500"
                          style={{
                            height: `${(h / maxBar) * 100}%`,
                            background: `rgba(14,165,233,${0.3 + i * 0.1})`,
                            transitionDelay: `${i * 80}ms`,
                          }}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 text-center">Últimos 7 dias</p>
                  </div>
                )}
              </div>

              {/* Input area */}
              <div className="px-5 pb-5">
                <div className="flex items-center gap-3 bg-muted/40 border border-border rounded-xl px-4 py-3">
                  <input
                    type="text"
                    placeholder="Pergunte qualquer coisa sobre seu negócio..."
                    className="flex-1 bg-transparent text-sm text-foreground placeholder-muted-foreground outline-none"
                    readOnly
                  />
                  <button className="w-8 h-8 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center hover:bg-primary/30 transition-colors">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                      <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}